import { SwitchButton } from "components/switchButton"
import { Link } from "react-router-dom"
import Button from "components/button"
import { EyeIcon, PencilIcon } from "@heroicons/react/outline"
import {
  formatNumber,
  getEventAddonQuantityModeLabel,
  getEventAddonScopeLabel,
  getEventAddonTypeLabel,
  isHaveActionPermission,
} from "utills/helpers"
import { TooltipParagraph } from "components/tooltipParagraph"
import { Tag } from "components/tag"
import { capitalize } from "lodash"
import { HistoryLogButton } from "components/historyLogButton"

interface IEventAddon {
  id: string
  name_en: string
  name_vi: string
  desc_en: string
  desc_vi: string
  base_price: number
  stock_total: number | null
  stock_remaining: number | null
  status: string
  type: string
  scope: string
  position: number
  required: boolean
  is_sold_out: boolean
  qty_mode: string
  qty_min: number
  qty_max: number
  has_note: boolean
  note_required: boolean
  low_stock_threshold: number
}

interface EventAddonTableProps {
  data?: IEventAddon[]
  pageSize: number
  currentPage: number
  _toggleSoldOut: (id: string, isSoldOut: boolean) => void
}

export const EventAddonTable = ({
  data = [],
  pageSize = 10,
  currentPage = 1,
  _toggleSoldOut,
}: EventAddonTableProps) => {
  return (
    <div className="flow-root max-w-full">
      <div className="relative overflow-x-auto min-w-full">
        <table className="table-auto w-max min-w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
          <thead>
            <tr>
              <th scope="col" className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8">
                No
              </th>
              <th scope="col" className="th">
                Name (VI)
              </th>
              <th scope="col" className="th">
                Name (EN)
              </th>
              <th scope="col" className="th">
                Type
              </th>
              <th scope="col" className="th">
                Scope
              </th>
              <th scope="col" className="th">
                Quantity Mode
              </th>
              <th scope="col" className="th">
                Base Price
              </th>
              <th scope="col" className="th">
                Stock
              </th>
              <th scope="col" className="th">
                Stock Remaining
              </th>
              <th scope="col" className="th">
                Status
              </th>
              <th scope="col" className="th">
                Sold Out
              </th>
              <th scope="col" className="th w-32">
                <span className="sr-only">Action</span>
                <p className="">Action</p>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((item: IEventAddon, index: number) => {
              const canEdit = isHaveActionPermission("addons", "events", "edit")

              return (
              <tr key={index}>
                <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                  {(currentPage - 1) * pageSize + 1 + index}
                </td>
                <td className="td max-w-10 overflow-x-auto">
                  <TooltipParagraph
                    text={item.name_vi}
                    className="whitespace-pre-wrap max-w-[280px]"
                  />
                </td>
                <td className="td max-w-10 overflow-x-auto">
                  <TooltipParagraph
                    text={item.name_en}
                    className="whitespace-pre-wrap max-w-[280px]"
                  />
                </td>
                <td className="td overflow-x-auto">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getEventAddonTypeLabel(item.type)}
                  </span>
                </td>
                <td className="td overflow-x-auto">
                  {getEventAddonScopeLabel(item.scope)}
                </td>
                <td className="td overflow-x-auto">
                  {getEventAddonQuantityModeLabel(item.qty_mode)}
                </td>
                <td className="td overflow-x-auto">
                  {formatNumber(item.base_price)}
                </td>
                <td className="td overflow-x-auto">
                  <span className={`font-medium ${item.stock_total !== null && item.stock_total <= item.low_stock_threshold ? 'text-red-600' : 'text-green-600'}`}>
                    {item.stock_total ?? "Unlimited"}
                  </span>
                </td>
                <td className="td overflow-x-auto">
                  <span className={`font-medium ${item.stock_remaining !== null && item.stock_remaining <= item.low_stock_threshold ? 'text-red-600' : 'text-green-600'}`}>
                    {item.stock_remaining ?? "Unlimited"}
                  </span>
                </td>
                <td className="td overflow-x-auto">
                  <Tag text={capitalize(item.status)} type={item.status === "active" ? "green" : "pink"} />
                </td>
                <td className="td overflow-x-auto">
                  <SwitchButton
                    checked={item.is_sold_out}
                    disabled={!canEdit}
                    onChange={() => _toggleSoldOut(item.id, item.is_sold_out)}
                  />
                </td>
                <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8">
                  <div className="flex w-[120px] items-center gap-2">
                    <HistoryLogButton
                      className="!w-8 !h-8 border-none text-black p-0 bg-gray-200 hover:bg-gray-200 active:bg-gray-300"
                      resource={`ws-event-addons/${item.id}/history`}
                      hideTitle
                    />
                    <Link
                      to={`/events/addons/${item.id}`}
                      className="w-8 h-8"
                    >
                      <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                        <EyeIcon className="w-4 h-4 text-black" />
                      </Button>
                    </Link>
                    {canEdit ? (
                      <Link
                        to={`/events/addons/${item.id}/edit`}
                        className="w-8 h-8"
                      >
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <PencilIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : null}
                  </div>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
